import React, { useMemo } from "react";
import {
    Box,
    Typography,
    Divider,
    IconButton,
    CardActions,
    Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import FullscreenOutlined from "@mui/icons-material/FullscreenOutlined";
import CameraTimeout from "./CameraTimeout";

type Props = {
    data: any;
    label: string;
    status: "idle" | "connecting" | "streaming" | "timeout";
    onClose?: () => void;
};

const FlespiPhotoViewer: React.FC<Props> = ({
    data,
    label,
    status,
    onClose,
}) => {
    // parse seguro
    const parsedData = useMemo(() => {
        if (!data) return null;
        if (typeof data === "string") {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        }
        return data;
    }, [data]);

    const photoUrl =
        parsedData?.result?.[0]?.response?.url ??
        parsedData?.result?.[0]?.response?.photo_url ??
        null;

    // DOWNLOAD
    const downloadFromUrl = async (url: string, filename: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Erro ao baixar arquivo:", err);
        }
    };

    // FULLSCREEN
  const onHandleFullScreen = (fileUrl: string, fileName: string) => {
        if (!document.fullscreenElement) {
            const element = document.createElement("img");
            element.className = "img-anexo";
            element.id = fileName;
            element.src = fileUrl;
            element.style.objectFit = "contain";
            document.body.appendChild(element);

            const img = document.querySelector(".img-anexo");
            img?.requestFullscreen().catch((err: any) => {
                console.error("Erro ao entrar em modo fullscreen:", err);
            });

            img?.addEventListener("fullscreenchange", () => {
                if (!document.fullscreenElement) {
                    document.body.removeChild(img);
                }
            });
        } else {
            document.exitFullscreen();
        }
    };

    return (
        <Box
            className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-gtr-surface tw-text-gtr-text"
            sx={{
                borderRadius: 2,
                overflow: "hidden",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* HEADER */}
            <Box
                className="tw-bg-gtr-sidebar tw-text-white"
                sx={{
                    px: 2,
                    py: 1.5,
                    backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
                    color: "common.white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontFamily: "Prometo", fontWeight: 700 }}>Captura de foto</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.82 }}>{label}</Typography>
                </Box>

                {onClose && (
                    <Tooltip title="Fechar" arrow>
                        <IconButton aria-label="Fechar captura de foto" onClick={onClose} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Divider />

            {/* CONTENT */}
            <Box
                className="tw-min-h-[280px] tw-bg-slate-950"
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#000",
                }}
            >
                {status === "streaming" && photoUrl && (
                    <img
                        src={photoUrl}
                        alt={label}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />
                )}
            </Box>

            <Divider />

            {/* FOOTER */}
            <Box className="tw-border-t tw-border-gtr-border/60 tw-bg-gtr-surface tw-p-3" sx={{ p: 1.5 }}>
                {status === "streaming" && photoUrl && (
                    <>
                        {/*<Typography variant="body2">
                            <b>URL:</b> {photoUrl}
                        </Typography>*/}

                        <CardActions
                            className="tw-flex tw-justify-end tw-gap-2"
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                                p: 0
                            }}
                        >
                            <Tooltip title="Baixar imagem" arrow>
                                <IconButton
                                    aria-label="Baixar imagem"
                                    onClick={() =>
                                        downloadFromUrl(photoUrl, `${label}.jpg`)
                                    }
                                    sx={{
                                        color: "#00B700",
                                        backgroundColor: "rgba(0,183,0,0.08)",
                                        "&:hover": { backgroundColor: "rgba(0,183,0,0.16)", transform: "translateY(-1px)" },
                                    }}
                                >
                                    <DownloadOutlined />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Visualizar em tela cheia" arrow>
                                <IconButton
                                    aria-label="Visualizar imagem em tela cheia"
                                    onClick={() => onHandleFullScreen(photoUrl, `${label}.jpg`)}
                                    sx={{
                                        color: "#006C77",
                                        backgroundColor: "rgba(0,108,119,0.08)",
                                        "&:hover": { backgroundColor: "rgba(0,108,119,0.16)", transform: "translateY(-1px)" },
                                    }}
                                >
                                    <FullscreenOutlined />
                                </IconButton>
                            </Tooltip>
                        </CardActions>
                    </>
                )}
                {status === "timeout" && <CameraTimeout onRetry={() => { }} />}
            </Box>
        </Box>
    );
};

export default FlespiPhotoViewer;
